// App.js
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom';

import { useAuth } from './context/AuthContext';
import './app-base.css';
import Sidebar from './components/sidebar/Sidebar';
import Login from './components/login/Login';
import Landing from './components/landing/Landing';
import SkillEditor from "./components/transform/SkillEditor";
import TransformEditor from "./components/transform/TransformEditor";
import { identity, API_BASE_URL } from './chronicles-ui/identity';
import './App.css';
import CharacterEditor from "./components/character/CharacterEditor";
import {CharacterProvider} from "./components/character/CharacterProvider";
import CharacterSheetPrintPage from "./components/character/CharacterSheetPrintPage";
import EventManager from "./components/eventManager/EventManager";
import EventBrowser from "./components/eventManager/EventBrowser";
import EventCheckoutScanner from "./components/checkout/EventCheckoutScanner";
import { expectedApiVersion } from './apiCompatibility';

// The printable character sheet (see CharacterSheetPrintPage) is a bare page
// with no sidebar/app chrome, matching what actually prints — so it's
// special-cased below to render outside the normal sidebar+Routes layout.
const PRINT_ROUTE_PATTERN = '/character/:slug/print';

// Lazy-loaded so markdown-it (and the rulebook reader) only download on demand.
const Rulebook = lazy(() => import('./components/rulebook/Rulebook'));


// Outside prod builds, always surface which API we're talking to (see the
// banner effect below) so a dev never has to guess from REACT_APP_API_URL.
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Compare major.minor of two semver strings.
 * Returns true if they match, false otherwise.
 */
function isVersionCompatible(actual, expected) {
    if (!actual || !expected) return false;
    const [aMaj, aMin] = actual.split('.').map(Number);
    const [eMaj, eMin] = expected.split('.').map(Number);
    return aMaj === eMaj && aMin === eMin;
}

/**
 * Parse a player's first name out of their "first last" name field and
 * normalize capitalization (players often enter it all lowercase).
 */
function toFirstName(fullName) {
    if (!fullName) return null;
    const first = fullName.trim().split(/\s+/)[0];
    if (!first) return null;
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

const App = () => {
    const { token, user, status, sessionVersion, logout, refreshIdentity } = useAuth();
    const staffPermissions = user?.staffPermissions || false;
    const playerId = user?.playerId ?? null;
    const firstName = toFirstName(user?.displayName);
    const characterName = user?.primaryCharacter?.name || null;
    const [apiBanner, setApiBanner] = useState(null);

    // Check API version compatibility on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await identity.request('/version', { authenticated: false });
                const apiVersion = res.data?.version;
                if (!apiVersion) {
                    setApiBanner({ message: `Could not determine API version (${API_BASE_URL}).`, isWarning: true });
                } else if (!isVersionCompatible(apiVersion, expectedApiVersion)) {
                    setApiBanner({
                        message: `API version mismatch: server is v${apiVersion}, this app expects v${expectedApiVersion}.x (${API_BASE_URL}). Some features may not work correctly.`,
                        isWarning: true,
                    });
                } else if (!isProduction) {
                    setApiBanner({ message: `Connected to API at ${API_BASE_URL} (v${apiVersion}).`, isWarning: false });
                } else {
                    setApiBanner(null);
                }
            } catch {
                setApiBanner({ message: `Could not connect to API at ${API_BASE_URL}.`, isWarning: true });
            }
        })();
    }, []);


    return (
        <Router>
            <AppShell
                key={sessionVersion}
                identityStatus={status}
                refreshIdentity={refreshIdentity}
                token={token}
                playerId={playerId}
                staffPermissions={staffPermissions}
                apiBanner={apiBanner}
                setApiBanner={setApiBanner}
                characterName={characterName}
                firstName={firstName}
                onLogout={logout}
            />
        </Router>
    );
};

/**
 * Everything that depends on the current route lives here (as opposed to in
 * App itself) because it needs useLocation, which only works below <Router>.
 */
const AppShell = ({ identityStatus, refreshIdentity, token, playerId, staffPermissions, apiBanner, setApiBanner, characterName, firstName, onLogout }) => {
    const location = useLocation();
    if (identityStatus === 'initializing' || identityStatus === 'unavailable') {
        return <div role="status" style={{ padding: '2rem' }}>
            {identityStatus === 'initializing' ? 'Checking sign-in…' : <>
                Sign-in could not be verified. Please try again.
                <button onClick={refreshIdentity}>Retry</button>
            </>}
        </div>;
    }
    const printMatch = matchPath(PRINT_ROUTE_PATTERN, location.pathname);

    // The printable sheet renders standalone — no banner, no sidebar — so it
    // matches what actually prints and so a refresh mid-preview doesn't flash
    // the full app shell around it.
    if (token && printMatch) {
        return <CharacterSheetPrintPage playerId={playerId} slug={printMatch.params.slug} />;
    }

    return (
        <div className="app-container">
            {apiBanner && (
                <div className={`version-warning-banner${apiBanner.isWarning ? '' : ' version-warning-banner--info'}`}>
                    {apiBanner.isWarning ? '⚠' : 'ℹ'} {apiBanner.message}
                    <button className="version-warning-dismiss" onClick={() => setApiBanner(null)}>✕</button>
                </div>
            )}
            <div className="main-layout">
                <div className="sidebar-container">
                    <Sidebar
                        staffPermissions={staffPermissions}
                        token={token}
                        characterName={characterName}
                        firstName={firstName}
                        onLogout={onLogout}
                    />
                </div>
                <div className="content-area">
                    <Suspense fallback={<div style={{ padding: '2rem', color: '#9a9385' }}>Loading…</div>}>
                    <Routes>
                        <Route path="/" element={<Landing token={token} characterName={characterName} firstName={firstName} />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/schedule" element={<EventBrowser token={token}/>} />
                        <Route path="/rulebook" element={<Rulebook />} />

                        {token ? (
                            <>
                                <Route path="/character" element={
                                    <CharacterProvider>
                                        <CharacterEditor token={token} playerId={playerId} staffPermissions={staffPermissions} />
                                    </CharacterProvider>
                                } />
                                <Route path="/character/:slug" element={
                                    <CharacterProvider>
                                        <CharacterEditor token={token} playerId={playerId} staffPermissions={staffPermissions} />
                                    </CharacterProvider>
                                } />
                                <Route path="/skills" element={<SkillEditor token={token}/>} />
                                <Route path="/transform" element={staffPermissions ? <TransformEditor token={token}/> : <Navigate to="/" replace />} />
                                <Route path="/transform/:slug" element={staffPermissions ? <TransformEditor token={token}/> : <Navigate to="/" replace />} />
                                <Route path="/event-manager" element={<EventManager token={token}/>} />
                                <Route path="/event-manager/:eventId" element={<EventManager token={token}/>} />
                                <Route path="/event-manager/:eventId/manage" element={<EventManager token={token}/>} />
                                <Route path="/event-manager/:eventId/checkout" element={<EventManager token={token}/>} />
                                <Route path="/event-checkout" element={<EventCheckoutScanner token={token}/>} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </>
                        ) : (
                            <Route path="*" element={<Navigate to={/^\/(?:character|skills|transform|event-manager|event-checkout)(?:\/|$)/.test(location.pathname)
                                ? `/login?${new URLSearchParams({ returnTo: `${location.pathname}${location.search}${location.hash}` })}` : '/'} replace />} />
                        )}
                    </Routes>
                    </Suspense>
                </div>
            </div>
        </div>
    );
};

export default App;
